import classNames from 'classnames'
import { useEffect, useMemo, useRef, useState } from 'react'

import { generateId } from 'core-components/helpers/generateId'
import { Button } from 'core-components/library/button'
import { Dropdown } from 'core-components/library/dropdown'
import { ICondition, IEditor } from 'core-components/library/filter'
import { Input } from 'core-components/library/inputs'
import { Tooltip } from 'core-components/library/tooltip'
import { BaseCrud, Operator, Static } from 'core-components/types'

import styles from './Condition.module.scss'

interface IProps {
  allowCreate?: boolean
  isConditionRemovable: boolean
  condition: ICondition
  onConditionChange: (value: ICondition) => void
  onConditionDelete: () => void
  editors: {
    [propname: string]: IEditor
  }
}

const allOperators = Object.values(Operator)

export const Condition = ({
  allowCreate,
  condition,
  editors,
  onConditionDelete,
  onConditionChange,
  isConditionRemovable,
}: IProps) => {
  const [fields, setFields] = useState<string[]>([])
  const keyRef = useRef<string>(generateId('condition'))

  const editorInfo = editors[condition.field]
  const Editor = editorInfo?.editor || Input

  const fieldCrud = useMemo(() => {
    const data = fields.map((el: string) => {
      return { key: el, value: el }
    })

    if (allowCreate && condition.field && !fields.includes(condition.field)) {
      data.push({ key: condition.field, value: condition.field })
    }

    return new BaseCrud({
      fetcher: new Static(data),
      keyField: 'key',
      defaultPaging: { page: 0, pageSize: 50 },
    })
  }, [allowCreate, condition.field, fields])

  const conditionsCrud = useMemo(() => {
    const operators = editorInfo?.conditions.length ? editorInfo.conditions : allOperators

    return new BaseCrud({
      fetcher: new Static(
        operators.map((el: Operator) => {
          return { key: el, value: el }
        })
      ),
      keyField: 'key',
      defaultPaging: { page: 0, pageSize: 50 },
    })
  }, [editorInfo?.conditions])

  const deleteField = () => {
    onConditionDelete()
  }

  const onFieldChange = (newField: string) => {
    const newCondition = {
      id: condition.id,
      field: newField,
      operator: '' as Operator,
      value: '',
    }
    onConditionChange(newCondition)
  }

  const onOperatorChange = (newOperator: Operator) => {
    const newCondition = {
      ...condition,
      operator: newOperator,
    }
    onConditionChange(newCondition)
  }

  const onValueChange = (newValue: string | string[] | number | null) => {
    const newCondition = {
      ...condition,
      value: newValue,
    }
    onConditionChange(newCondition)
  }

  useEffect(() => {
    setFields(Object.keys(editors))
  }, [editors])

  return (
    <div className={classNames(styles.root)}>
      <div className={styles.fields}>
        <div className={classNames(styles.group, styles.group__field)}>
          <p className={styles.group__label}>Field</p>
          <Dropdown
            __testId={'conditionFieldDropdown'}
            __targetTestId={'conditionFieldTarget'}
            allowCreate={allowCreate}
            onCreate={(newValue) => {
              setFields([...fields, newValue])
              onFieldChange(newValue)
            }}
            source={fieldCrud}
            caption={'value'}
            value={condition.field}
            onChange={(value) => onFieldChange(value)}
          />
        </div>
        <div className={classNames(styles.group, styles.group__condition)}>
          <p className={styles.group__label}>Condition</p>
          <Dropdown
            __testId={'conditionOperatorDropdown'}
            __targetTestId={'conditionOperatorTarget'}
            source={conditionsCrud}
            disabled={!condition.field}
            caption={'value'}
            value={condition.operator}
            onChange={(value) => onOperatorChange(value as Operator)}
          />
        </div>
        <div className={classNames(styles.group, styles.group__value)}>
          <p className={styles.group__label}>Value</p>
          <Editor
            key={keyRef.current}
            condition={condition.operator}
            value={condition.value}
            disabled={!condition.field}
            onChange={(value) => {
              onValueChange(value)
            }}
          />
        </div>
        <div className={styles.action_delete}>
          <Tooltip tipContent={() => <div data-testid="deleteTooltipContent">Delete</div>}>
            <Button
              __testId={'DeleteCondition'}
              disabled={!isConditionRemovable}
              iconButton="kbase kbase-delete-alt"
              color="danger"
              onClick={deleteField}
            />
          </Tooltip>
        </div>
      </div>
    </div>
  )
}
